import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2, } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { UserAppService } from 'src/app/services/user-app.service';
import { UserStateService } from 'src/app/services/user-state.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'home-module',
  template: `
    <mat-sidenav-container [autosize]="true">
      <mat-sidenav
        #sidenav
        mode="side"
        [disableClose]="true"
        class="min-h-[calc(100vh)]"
        [opened]="!notLoggedInStatus"
        [fixedInViewport]="options.value.fixed"
        [fixedTopGap]="options.value.top"
        [fixedBottomGap]="options.value.bottom"
        *ngIf="!notLoggedInStatus"
      >
        <app-side-nav (dataEmitter)="receiveData($event)"></app-side-nav>
      </mat-sidenav>

      <mat-sidenav-content class="shell-content" [class.has-webchat]="webchatActive">
        <div class="topbar" *ngIf="!notLoggedInStatus">
          <button
            type="button"
            class="acct"
            [matMenuTriggerFor]="acctMenu"
            [matTooltip]="displayName"
            aria-label="Account menu"
          >
            <span class="avatar">{{ initials }}</span>
          </button>
          <mat-menu #acctMenu="matMenu">
            <button mat-menu-item (click)="logout()">Log Out</button>
          </mat-menu>
        </div>
        <router-outlet class="justify-self-center"></router-outlet>
        <div class="fixed bottom-4 right-8 webchat-toast" *ngIf="loading">
          <div class="flex flex-col justify-center items-center">
            <mat-spinner [diameter]="60"></mat-spinner>
            <p class="webchat-toast-text">WebChat Loading...</p></div>
        </div>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [
    `
      .shell-content {
        background: #f7f8fa;
        min-height: 100vh;
        font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto,
          Arial, sans-serif;
      }
      /* clearance so bottom-of-page controls (e.g. Save bars) can scroll
         above the fixed webchat launcher instead of hiding under it.
         border-box keeps min-height:100vh inclusive of the padding, so short
         pages don't gain a scrollbar just from the clearance. */
      .shell-content.has-webchat {
        box-sizing: border-box;
        padding-bottom: 170px;
      }
      .webchat-toast {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        padding: 16px 20px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      }
      .webchat-toast-text {
        margin-top: 8px;
        color: #6b7280;
        font-size: 14px;
        font-weight: 500;
      }
      .topbar {
        height: 58px;
        background: #fff;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: 0 24px;
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 10;
      }
      .acct {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        margin: 0;
        background: none;
        border: 1px solid #e5e7eb;
        border-radius: 50%;
        cursor: pointer;
      }
      .acct:hover {
        border-color: #cbd0d8;
        background: #f3f4f6;
      }
      .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #e60000;
        color: #fff;
        font-size: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
    `,
  ],
})
export class HomeModuleComponent implements OnInit, OnDestroy {
  options = this._formBuilder.group({
    bottom: 0,
    fixed: true,
    top: 0,
  });

  title = 'SALES AI';
  name = environment.name;
  isOpenedInBigScreen = true;
  collapsed: boolean = false;
  notLoggedInStatus: boolean = true;
  sideNavDisabled = true
  loading = false;
  webchatActive = false;
  displayName = '';
  initials = '';
  sectionLabel = 'Home';
  private scriptId = 'externalScriptawsdrvhtiv12gnmtb688';

  constructor(
    private userService: UserAppService,
    private userStateService: UserStateService,
    private cdr: ChangeDetectorRef,
    private _formBuilder: FormBuilder,
    private renderer: Renderer2,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  ngOnInit() {
    // Use cached user info from UserStateService for faster loading
    const userInfo = this.userStateService.getCurrentUserInfo();

    if (!userInfo || !('username' in userInfo)) {
      this.notLoggedInStatus = true;
      this.removeScript();
    } else {
      this.notLoggedInStatus = false;
      this.sideNavDisabled = userInfo?.groups?.length === 1 && userInfo?.groups.includes('sales_rep');

      // Load webchat
      this.userService.getWebChat().subscribe({
        next: (response: any) => {
          if (response?.webchat_build_url) {
            this.loading = true;

            this.loadScript(response.webchat_build_url)
              .finally(() => {
                this.loading = false;
              })
              .then(() => {
                console.info('Webchat is loaded');
                this.webchatActive = true;
                this.cdr.detectChanges();
              })
              .catch((error) => {
                console.error('Error loading webchat:', error);
              });
          }
        },
      });

      // Store user info in localStorage
      let temp: any = { name: userInfo?.username, email: userInfo?.email };
      localStorage.setItem('currentUser', JSON.stringify(temp));

      this.setUserDisplay(userInfo);
    }

    // Breadcrumb section label, derived from the router URL
    this.sectionLabel = this.deriveSection(this.router.url);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.sectionLabel = this.deriveSection(this.router.url);
      });

    this.cdr.detectChanges();
  }

  private setUserDisplay(userInfo: any) {
    const first = (userInfo?.first_name || '').trim();
    const last = (userInfo?.last_name || '').trim();
    if (first || last) {
      this.displayName = `${first} ${last}`.trim();
      this.initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    } else {
      this.displayName = userInfo?.username || '';
      this.initials = (this.displayName.charAt(0) || '?').toUpperCase();
    }
  }

  private deriveSection(url: string): string {
    if (url.includes('data_management')) return 'Data Sources';
    if (url.includes('manage-solutions') || url.includes('customer'))
      return 'My Solutions';
    if (url.includes('marketplace') || url.includes('available-solutions'))
      return 'Marketplace';
    if (url.includes('settings')) return 'Settings';
    return 'Home';
  }

  logout() {
    this.userService.logout().subscribe({
      next: () => {
        this.userStateService?.clearUserInfo?.();
        this.router.navigateByUrl('/account');
      },
      error: (error: any) => {
        if (error?.status === 401 || error?.status === 403) {
          this.router.navigateByUrl('/account');
        }
      },
    });
  }

  ngOnDestroy() {
    this.removeScript();
  }

  receiveData(data: boolean) {
    this.collapsed = data;
  }


  loadScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.async = true;
      script.id = this.scriptId;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        console.error('Error loading script');
        reject(new Error(`Failed to load script: ${url}`));
      };

      this.renderer.appendChild(document.body, script);
    });
  }

  removeScript() {
    const script = this.document.getElementById(this.scriptId);
    if (script) {
      this.renderer.removeChild(this.document.body, script);
      window.location.reload();
    }
  }
}
